/**
 * `is_signature_reusable` é um controle exclusivamente do frontend: a API apenas
 * armazena e devolve a flag, sem agir sobre ela. Quando o signer marca que a
 * assinatura NÃO deve ser reaproveitada, ela vale só para o documento que estava
 * sendo assinado — nos documentos seguintes é como se ele não tivesse assinatura
 * nenhuma e precisa criar uma nova.
 *
 * Por isso "o signer já tem assinatura?" nunca é `has_signature` sozinho.
 *
 * A comparação é `=== true` de propósito: signers antigos podem vir sem a flag
 * (`null`/ausente), e nesse caso tratamos como não-reutilizável.
 */
export const hasUsableSignature = (signer, type = "signature") => {
  const hasStoredImage = type === "initial" ? signer?.has_initial === true : signer?.has_signature === true

  return hasStoredImage && signer?.is_signature_reusable === true
}

/**
 * `GET /signature/{type}` usa `responseType: 'blob'`, então em caso de erro o
 * axios NÃO parseia o corpo: `error.response.data` é um Blob, e ler `.message`
 * direto dele devolve sempre `undefined`. Para saber o motivo real da falha é
 * preciso ler o Blob como texto e parsear o JSON à mão.
 */
export const readApiErrorMessage = async (error) => {
  const data = error?.response?.data

  if (data instanceof Blob) {
    try {
      return JSON.parse(await data.text())?.message
    } catch {
      return undefined
    }
  }

  return typeof data?.message === "string" ? data.message : undefined
}

/**
 * `has_signature`/`has_initial` podem estar `true` no signer mas a imagem não
 * existir mais no storage. Nesse caso `GET /signature/{type}` responde 500 com
 * `"File not found at path: acc-.../signer-...-signature_v....png"` (não 404),
 * e o erro precisa ser tratado como "não tem assinatura" para o fluxo oferecer
 * a criação de uma nova.
 *
 * O 500 SÓ conta quando a mensagem confirma que o arquivo não existe. Um 500
 * genérico (banco fora, timeout no storage) não pode entrar aqui: o fallback
 * faz o signer desenhar outra assinatura e sobrescrever a que estava salva —
 * perder a assinatura reutilizável por causa de um erro transitório é pior que
 * mostrar "tente novamente".
 *
 * É async por causa do Blob — ver `readApiErrorMessage`.
 */
export const isMissingStoredSignatureError = async (error) => {
  const status = error?.response?.status

  if (status === 404 || status === 410) return true
  if (status !== 500) return false

  const message = await readApiErrorMessage(error)

  return typeof message === "string" && message.toLowerCase().includes("file not found")
}
